/**
 * Deriving what the UI shows from what the database stores.
 *
 * The central rule, matching the Python domain and the reminder engine:
 *
 *     actionBy = expiry_date - notice_period_days
 *
 * Everything the user sees — ordering, urgency, "days remaining" — is measured
 * against `actionBy`, not the expiry date. A contract expiring 12 Mar with 30
 * days' notice needs action by 10 Feb, and sorting by expiry would bury it
 * below things that are genuinely less urgent.
 */

import { addDays, daysBetween, parseDateOnly, today } from './dates'
import { annualisedMinor, monthlyMinor } from './money'
import type { DerivedItem, ItemRecord, Kind, Urgency } from './types'

export function urgencyOf(daysRemaining: number): Urgency {
  if (daysRemaining < 0) return 'overdue'
  if (daysRemaining === 0) return 'today'
  if (daysRemaining <= 30) return 'soon'
  return 'later'
}

export function derive(item: ItemRecord, from: Date = today()): DerivedItem {
  const expiryDate = parseDateOnly(item.expiry_date)
  const actionBy = addDays(expiryDate, -item.notice_period_days)
  const daysRemaining = daysBetween(from, actionBy)
  return {
    item,
    expiryDate,
    actionBy,
    daysRemaining,
    urgency: urgencyOf(daysRemaining),
    monthlyMinor: monthlyMinor(item),
    annualMinor: annualisedMinor(item),
  }
}

/** Soonest deadline first, name as a stable tiebreaker. */
export function byUrgency(a: DerivedItem, b: DerivedItem): number {
  return (
    a.actionBy.getTime() - b.actionBy.getTime() || a.item.name.localeCompare(b.item.name)
  )
}

export const URGENCY_GROUPS = [
  { key: 'overdue', title: 'Overdue', hint: 'These needed action already' },
  { key: 'today', title: 'Today', hint: 'Act today' },
  { key: 'soon', title: 'Next 30 days', hint: 'Coming up' },
  { key: 'later', title: 'Later', hint: 'Nothing to do yet' },
] as const

export interface UrgencyGroup {
  key: Urgency
  title: string
  hint: string
  items: DerivedItem[]
}

/** Group for the dashboard, dropping empty groups so the page stays quiet. */
export function groupByUrgency(items: DerivedItem[]): UrgencyGroup[] {
  return URGENCY_GROUPS.map((group) => ({
    ...group,
    items: items.filter((i) => i.urgency === group.key).sort(byUrgency),
  })).filter((group) => group.items.length > 0)
}

/**
 * Whether a kind has a cost worth asking about.
 *
 * A passport has a renewal fee but it is not recurring spend, so the cost
 * fields are hidden for documents to keep the form short.
 * "Other" can be anything (miles, warranties, trials), so cost is available.
 */
export function kindHasCost(kind: Kind): boolean {
  return kind !== 'document'
}

/** Whether a kind typically has a cancellation notice period. */
export function kindHasNotice(kind: Kind): boolean {
  return kind !== 'document'
}

/** A sensible starting point when adding a record of each kind. */
export function defaultsForKind(kind: Kind): {
  notice_period_days: number
  auto_renews: boolean
  renewal_period: 'none' | 'monthly' | 'quarterly' | 'annual'
  billing_cycle: 'one_off' | 'monthly' | 'quarterly' | 'annual'
} {
  switch (kind) {
    case 'document':
      // Passport, licence, MOT — one date, no bill, no notice window.
      return {
        notice_period_days: 0,
        auto_renews: false,
        renewal_period: 'none',
        billing_cycle: 'one_off',
      }
    case 'subscription':
      // Netflix, gym, SaaS — usually monthly, rolls on unless you cancel.
      return {
        notice_period_days: 0,
        auto_renews: true,
        renewal_period: 'monthly',
        billing_cycle: 'monthly',
      }
    case 'contract':
      // Broadband, energy, phone lock-in — 30-day notice is the common case.
      // Auto-renew off so the user decides at the end of the term.
      return {
        notice_period_days: 30,
        auto_renews: false,
        renewal_period: 'none',
        billing_cycle: 'monthly',
      }
    case 'other':
      // Miles, warranties, trials, gift cards — flexible defaults; user can
      // fill in cost / notice if they apply.
      return {
        notice_period_days: 0,
        auto_renews: false,
        renewal_period: 'none',
        billing_cycle: 'one_off',
      }
  }
}
